import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { NameIdOption } from "../../../common/types/commonTypes";
import { selectActiveExpertUserOptions } from "../../user/expertUsersSlice";
import { formatCitiesToNameIdOptions } from "../helpers";
import { fetchCities } from "../settingsActions";
import { resetRecruitmentAreaDetails } from "./recruitmentsSlice";
import { RecruitmentAreaForm } from "./types";

interface Props {
  close: () => void;
  onSubmit: (data: RecruitmentAreaForm) => void;
  isNew?: boolean;
  labelSize?: number;
  fieldSize?: number;
}

export const RecruitmentsAreaForm = ({
  onSubmit,
  close,
  isNew,
  labelSize = 2,
  fieldSize = 10,
}: Props) => {
  const [cityOptions, setCityOptions] = useState<NameIdOption[]>([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState<boolean>(false);

  const { status: expertsStatus } = useAppSelector(
    (state) => state.expertUsers,
  );
  const { status: recruitmentsStatus } = useAppSelector(
    (state) => state.recruitments.recruitmentAreaDetails,
  );
  const isExpertsLoading = expertsStatus === "pending";
  const isRecruitmentsLoading = recruitmentsStatus === "pending";

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, errors },
  } = useFormContext<RecruitmentAreaForm>();

  useEffect(() => {
    return () => {
      dispatch(resetRecruitmentAreaDetails());
    };
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expertOptions = useAppSelector((state) =>
    selectActiveExpertUserOptions(state),
  );

  useEffect(() => {
    setIsCitiesLoading(true);
    dispatch(fetchCities("")).then((action) => {
      if (isFulfilled(action))
        setCityOptions(formatCitiesToNameIdOptions(action.payload));
      setIsCitiesLoading(false);
    });
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDisabled = !isValid || !isDirty;

  return (
    <>
      <OverlaySpinner
        isLoading={isRecruitmentsLoading}
        sx={{ position: "absolute" }}
      />
      <FormGridRow
        label={t("common:name")}
        labelSize={labelSize}
        fieldSize={fieldSize}
      >
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              fullWidth
              size="small"
              error={!!errors.name}
            />
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("common:cities")}
        labelSize={labelSize}
        fieldSize={fieldSize}
      >
        <Controller
          name="cities"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CustomAutocompleteMulti
              sx={{
                // workaround to prevent component to resize on hover/focus
                "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
                  { minWidth: "30px" },
              }}
              name="cities"
              isLoading={isCitiesLoading}
              defaultValue={field.value}
              options={cityOptions}
              onChange={(_: any, newValue: AutocompleteType[]) =>
                field.onChange(newValue)
              }
              textFieldError={!!errors.cities}
              setTagDisabledForRemovedOptions={true}
            />
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("settings:experts")}
        labelSize={labelSize}
        fieldSize={fieldSize}
      >
        <Controller
          name="members"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CustomAutocompleteMulti
              sx={{
                "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
                  { minWidth: "30px" },
              }}
              name="members"
              isLoading={isExpertsLoading}
              defaultValue={field.value}
              options={expertOptions}
              onChange={(_: any, newValue: AutocompleteType[]) =>
                field.onChange(newValue)
              }
              textFieldError={!!errors.members}
              setTagDisabledForRemovedOptions={true}
            />
          )}
        />
      </FormGridRow>
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        sx={{ margin: "16px 0px 0px 16px" }}
      >
        {isNew && (
          <CustomButton
            startIcon={<CloseIcon />}
            variant="outlined"
            onClick={close}
            sx={{ height: "36px", borderRadius: "20px" }}
          >
            {t("common:cancelShort")}
          </CustomButton>
        )}
        <Box flexGrow={1} />
        <SpinnerButton
          label={t("save")}
          onClick={handleSubmit(onSubmit)}
          disabled={saveDisabled}
          variant="contained"
          isLoading={isRecruitmentsLoading}
          color="primary"
          sx={{ height: "36px", borderRadius: "20px" }}
        />
      </Box>
    </>
  );
};
